import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiRequest } from "../api";
import { useAuth } from "../auth";

type Overview = {
  stats: {
    totalClients: number;
    totalServices: number;
    totalBookings: number;
    upcomingBookings: number;
    newBookingRequests: number;
  };
  recentBookings: Array<{
    id: string;
    status: string;
    preferredStartsAt: string;
    service?: { name: string };
    client?: { user?: { fullName?: string; email?: string } };
  }>;
  setupProgress: {
    hasDescription: boolean;
    hasServices: boolean;
    hasWebsiteContent: boolean;
    isPublished: boolean;
  };
  websiteStatus: string;
};

export function OverviewPage() {
  const { businessId, business } = useAuth();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!businessId) return;
    void apiRequest<{ overview: Overview }>(
      `/businesses/${businessId}/overview`,
    )
      .then((data) => setOverview(data.overview))
      .catch((err: Error) => setError(err.message));
  }, [businessId]);

  if (!businessId) {
    return (
      <section>
        <p className="notice">No business found. Complete signup first.</p>
      </section>
    );
  }

  const stats = overview?.stats;

  return (
    <>
      <header className="page-header">
        <div>
          <p className="eyebrow">Overview</p>
          <h1>{business?.name ?? "Business dashboard"}</h1>
        </div>
        <div className="cta-row">
          <Link className="btn btn-primary" to="/app/onboarding">
            Continue setup
          </Link>
          {business?.slug ? (
            <Link
              className="btn btn-secondary"
              to={`/site/${business.slug}?preview=1`}
              target="_blank"
            >
              Preview website
            </Link>
          ) : null}
        </div>
      </header>

      {error ? <p className="notice error">{error}</p> : null}

      <section>
        <div className="stat-grid">
          <article className="stat-card">
            <span>Clients</span>
            <strong>{stats?.totalClients ?? 0}</strong>
          </article>
          <article className="stat-card">
            <span>Services</span>
            <strong>{stats?.totalServices ?? 0}</strong>
          </article>
          <article className="stat-card">
            <span>Bookings</span>
            <strong>{stats?.totalBookings ?? 0}</strong>
          </article>
          <article className="stat-card">
            <span>Upcoming</span>
            <strong>{stats?.upcomingBookings ?? 0}</strong>
          </article>
          <article className="stat-card">
            <span>New requests</span>
            <strong>{stats?.newBookingRequests ?? 0}</strong>
          </article>
        </div>
      </section>

      <section>
        <h2>Setup progress</h2>
        <ul>
          <li>
            Description:{" "}
            {overview?.setupProgress.hasDescription ? "Done" : "Needed"}
          </li>
          <li>
            Services: {overview?.setupProgress.hasServices ? "Done" : "Needed"}
          </li>
          <li>
            Website content:{" "}
            {overview?.setupProgress.hasWebsiteContent ? "Done" : "Needed"}
          </li>
          <li>
            Published:{" "}
            {overview?.setupProgress.isPublished
              ? "Live"
              : (overview?.websiteStatus ?? "Draft")}
          </li>
        </ul>
      </section>

      <section>
        <h2>Recent bookings</h2>
        <table className="data-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Service</th>
              <th>When</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(overview?.recentBookings ?? []).map((booking) => (
              <tr key={booking.id}>
                <td>{booking.client?.user?.fullName ?? "—"}</td>
                <td>{booking.service?.name ?? "—"}</td>
                <td>{new Date(booking.preferredStartsAt).toLocaleString()}</td>
                <td>{booking.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(overview?.recentBookings ?? []).length === 0 ? (
          <p className="notice">No bookings yet.</p>
        ) : null}
      </section>
    </>
  );
}
